import * as React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import ConfirmPopup from './ConfirmPopup';
import axios from 'axios';
import { toast } from 'react-toastify';
import authService from './api-authorization/AuthorizeService';

type CustomPriceList = {
    id: number;
    customerId: number;
    customerName: string;
    itemId: number;
    item: string;
    itemTypeId: number;
    hirePeriodWeeks: number;
    frequencyId: number;
    price: number;
    additionalHireRate: number;
    travelOneWayRate: number;
    isDirty: boolean
}

interface GenericList {
    id: number;
    name: string;
}

interface Item {
    id: number;
    name: string;
    typeId: number;
    defaultDays: number;
}

const AdminPriceListCustomData = (props: RouteComponentProps<{}>) => {
    const [pricelistLoading, setPricelistLoading] = React.useState(true);
    const [loading, setLoading] = React.useState(true);
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});
    const [customerOptions, setCustomerOptions] = React.useState<GenericList[]>([]);
    const [itemList, setItemList] = React.useState<Item[]>([]);
    const [frequencyList, setFrequencyList] = React.useState<GenericList[]>([]);
    const [priceListCustom, setPriceListCustom] = React.useState<CustomPriceList[]>([]);
    const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
    const [itemIndex, setItemIndex] = React.useState(-1);
    const [readOnly, setReadOnly] = React.useState(false);

    React.useEffect(() => {
        window.scrollTo(0, 0);

        getData();
    }, []);

    const getData = async () => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        axios.get('api/User/CheckUserPermission?Permission=AdminPricelistsView&SubId=' + user.sub, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data == true) {
                getPriceListData();
            } else if (res.data == false) {
                //redirect to 403 permission denied
                props.history.push("/accessdenied")
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }


    const getPriceListData = async () => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        axios.get('api/PriceList/GetPriceListCustom?SubId=' + user.sub, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            var plItems = res.data.priceListCustom;
            var newRow = addBlankRow();
            plItems.push(newRow);

            setItemList(res.data.itemList);
            var frequency: GenericList[] = res.data.frequencyList;
            frequency = frequency.filter(f => f.id < 4);
            setFrequencyList(frequency);
            setPriceListCustom(plItems);
            setCustomerOptions(res.data.customerOptions);
            setReadOnly(res.data.readOnly);
            setPricelistLoading(false);
            setLoading(false);
        }).catch(error => {
            toast.error(error.message);
        });
    }

    const getPriceListItems = async () => {
        const token = await authService.getAccessToken();

        axios.get('api/PriceList/GetPriceListCustomItems', {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            var plItems = res.data;
            var newRow = addBlankRow();
            plItems.push(newRow);

            setPriceListCustom(plItems);
            setPricelistLoading(false);
        }).catch(error => {
            toast.error(error.message);
        });
    }

    const addBlankRow = () => {
        let pricelistItem: CustomPriceList = {
            id: 0,
            customerId: -1,
            customerName: "",
            itemId: -1,
            itemTypeId: -1,
            item: "",
            hirePeriodWeeks: 1,
            frequencyId: -1,
            price: 0,
            additionalHireRate: 0,
            travelOneWayRate: 0,
            isDirty: false
        };

        return pricelistItem;
    }

    //HANDLE CHANGE
    const handleCellChange = (e: any, itemIndex: number) => {
        let error = errors;
        delete error["pricelistitems"];

        var newValue = e.target.value * 1;
        let plItems = [...priceListCustom];
        let plItem = plItems[itemIndex];

        let updatedPlItem: any = {};
        updatedPlItem = plItem;
        updatedPlItem[e.target.name] = newValue;
        updatedPlItem["isDirty"] = true;

        if (e.target.name === "itemId") {
            //get item type id
            var item = itemList.find(i => i.id === newValue);
            if (item) {
                updatedPlItem["itemTypeId"] = item.typeId;
                updatedPlItem["item"] = item.name;
            }
        }

        if (itemIndex == (priceListCustom.length - 1)) {
            //add new row
            var newRow = addBlankRow();
            plItems.push(newRow);
        }

        setPriceListCustom(plItems);
    }
    //END HANDLE CHANGE

    //DELETE PRICELIST ITEM
    const deleteItem = (e: any, itemIndex: number) => {
        e.preventDefault();

        setShowDeleteConfirm(true);
        setItemIndex(itemIndex);
    }

    const hideDeleteConfirm = () => {
        setShowDeleteConfirm(false);
        setItemIndex(-1);
    }

    const saveDeleteConfirm = () => {
        var pricelist = [...priceListCustom];
        var pricelistItem = pricelist[itemIndex];

        if (pricelistItem.id === 0) {
            //just remove from the list as not saved
            pricelist.splice(itemIndex, 1);
            setShowDeleteConfirm(false);
            setItemIndex(-1);
            setPriceListCustom(pricelist);

            toast.success("Item deleted successfully");
        } else {
            saveDeletePricelist(pricelist, pricelistItem.id);
        }
    }

    const saveDeletePricelist = async (pricelist: CustomPriceList[], pricelistItemId: number) => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var deletePriceList = {
            id: pricelistItemId,
            subId: user.sub
        };

        axios.post('api/PriceList/DeletePricelistCustomItem', deletePriceList, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                for (var i = 0; i < errors.length; i++) {
                    toast.error(errors[i].content);
                };
            } else {
                toast.success("Price List Saved Successfully");
                pricelist.splice(itemIndex, 1);
                setShowDeleteConfirm(false);
                setItemIndex(-1);
                setPriceListCustom(pricelist);
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }
    //END DELETE PRICELIST ITEM

    const validate = () => {
        let errorList: any = {};
        let formIsValid = true;
        let items = priceListCustom;

        if (priceListCustom.length === 1) {
            errorList["pricelistitems"] = "Please enter at least one item!";
            setErrors(errorList)
            return true;
        }

        var rowNumber = 0;
        var errorString = "Please enter the following values: ";
        var error = false;

        for (var i = 0; i < items.length - 1; i++) {
            rowNumber += 1;
            var newErrorString = "";

            if (items[i].customerId < 1) {
                newErrorString += "Customer, ";
                error = true;
            }

            if (items[i].itemId < 1) {
                newErrorString += "Item, ";
                error = true;
            }

            //No Hire Period or Additional Hire Rate for Delivery / Pickup Items for 1300 / Pump & Cleans
            if (items[i].itemTypeId != 44 && items[i].itemTypeId != 45 && items[i].itemTypeId != 26) {
                if (!items[i].hirePeriodWeeks || items[i].hirePeriodWeeks <= 0) {
                    newErrorString += "Hire Period, ";
                    error = true;
                }

                if (!items[i].additionalHireRate || items[i].additionalHireRate <= 0) {
                    newErrorString += "Additional Hire Rate, ";
                    error = true;
                }
            }

            if (!items[i].price || items[i].price <= 0) {
                newErrorString += "Price, ";
                error = true;
            }

            if (newErrorString != "") {
                errorString += "\n"
                errorString += "Row " + rowNumber + ": " + newErrorString;
                errorString = errorString.substring(0, errorString.length - 2);
            }

        }
        if (error) {
            formIsValid = false;
            errorList["pricelistitems"] = errorString;
        }
        setErrors(errorList);
        return formIsValid;
    }

    const save = (e: any) => {
        e.preventDefault();

        if (validate()) {
            var changed = priceListCustom.filter(p => p.isDirty);

            if (changed.length === 0) {
                toast.info("No items have changed.");
            } else {
                savePricelist(changed);
            }
        } else {
            toast.error("Please fix validation errors before Saving")
        }
    }

    const savePricelist = async (changed: CustomPriceList[]) => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var savePriceList = {
            priceListItems: changed,
            subId: user.sub
        };

        axios.post('api/PriceList/SavePriceListCustomData', savePriceList, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                for (var i = 0; i < errors.length; i++) {
                    toast.error(errors[i].content);
                };
            } else {
                toast.success("Price List Saved Successfully");
                setPricelistLoading(true);
                getPriceListItems();
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    const lastRow = priceListCustom.length - 1;
    const confirmPopup = <ConfirmPopup heading="Delete Price List Item?" text="Are you sure you want to delete this price list item?" hideConfirmModal={hideDeleteConfirm} showConfirmModal={showDeleteConfirm} saveConfirmModal={saveDeleteConfirm} />

    const renderDetails = (
        <div>
            <div className="static-modal">
                {confirmPopup}
            </div>
            <h1>Price List - Custom </h1>
            <div className={pricelistLoading ? "hidden" : ""}>
                <div className="overflowAuto">
                    <table className="table tableColours">
                        <thead>
                            <tr>
                                <th className="hidden">Id</th>
                                <th></th>
                                <th>Customer</th>
                                <th>Item</th>
                                <th>Maintenance</th>
                                <th>Hire Period (Weeks)</th>
                                <th>Price</th>
                                <th>Additional Weekly Rate</th>
                                <th>Travel One Way Rate</th>
                                <th></th>
                            </tr>
                        </thead>
                        {priceListCustom.map((item, index) =>
                            <tbody key={index}>
                                <tr>
                                    <td className="hidden">{item.id}</td>
                                    <td className="table__text--align">{index + 1}</td>
                                    <td className="table__text--align">
                                        <span className={item.id === 0 ? "" : "hidden"}>
                                            <select className="select" id="customerId" name="customerId" value={item.customerId} onChange={(e) => handleCellChange(e, index)}>
                                                <option defaultValue="-1"></option>
                                                {item.id === 0 ? customerOptions.map(cust =>
                                                    <option key={cust.id} value={cust.id}>{cust.name}</option>
                                                ) : ""};
                                            </select>
                                        </span>
                                        <span className={item.id === 0 ? "hidden" : ""}>{item.customerName}</span>
                                    </td>
                                    <td className="table__text--align">
                                        <select className="select" id="itemId" name="itemId" value={item.itemId} onChange={(e) => handleCellChange(e, index)}>
                                            <option defaultValue="-1"></option>
                                            {itemList.map(item =>
                                                <option key={item.id} value={item.id}>{item.name}</option>
                                            )};
                                        </select>
                                    </td>
                                    <td className="table__text--align">
                                        <select className={(item.itemTypeId === 25 || item.itemTypeId === 26) && !item.item.toLowerCase().includes("sewer connect") ? "select" : "hidden"} id="frequencyId" name="frequencyId" value={item.frequencyId} onChange={(e) => handleCellChange(e, index)}>
                                            <option defaultValue="-1"></option>
                                            {frequencyList.map(item =>
                                                <option key={item.id} value={item.id}>{item.name}</option>
                                            )};
                                        </select>
                                    </td>
                                    <td className="table__text--align">
                                        <input type='number' min="0" step="1" className={item.itemTypeId === 44 || item.itemTypeId === 45 || item.itemTypeId === 26 ? 'hidden' : 'form-control textalignright'} id="hirePeriodWeeks" name="hirePeriodWeeks" value={item.hirePeriodWeeks} onChange={(e) => handleCellChange(e, index)} />
                                    </td>
                                    <td className="table__text--align">
                                        <input type='number' min="0" step="0.01" className='form-control textalignright' id="price" name="price" value={item.price} onChange={(e) => handleCellChange(e, index)} />
                                    </td>
                                    <td className="table__text--align">
                                        <input type='number' min="0" step="0.01" className={item.itemTypeId === 44 || item.itemTypeId === 45 || item.itemTypeId === 26 ? 'hidden' : 'form-control textalignright'} id="additionalHireRate" name="additionalHireRate" value={item.additionalHireRate} onChange={(e) => handleCellChange(e, index)} />
                                    </td>
                                    <td className="table__text--align">
                                        <input type='number' min="0" step="0.01" className='form-control textalignright' id="travelOneWayRate" name="travelOneWayRate" value={item.travelOneWayRate} onChange={(e) => handleCellChange(e, index)} />
                                    </td>
                                    <td className="table__text--align">
                                        <div className={index === lastRow ? "hidden" : "delete--tablecell"}>
                                            <a className="makeitred" href="#" onClick={(e) => deleteItem(e, index)}>
                                                <span className="fas fa-times-circle edit--icon alignIconCenter"></span>
                                            </a>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        )}
                    </table>
                    {errors["pricelistitems"] ?
                        (errors["pricelistitems"]).split("\n").map((item: any, key: any) => {
                            return <span className="label errors" key={key}>{item}<br /></span>
                        })
                        : ""}
                </div>
            </div>
            <div className="colAlignRight marginBottom10">
                <button className={readOnly ? "hidden" : "defaultbutton defaultbutton__large"} type="button" onClick={save} disabled={pricelistLoading ? true : false}>Save</button>
            </div>
        </div>
    );

    let contents = loading
        ? <p><em>Loading...</em></p>
        : renderDetails;

    return (
        <div>
            {contents}
        </div>
    );
}

export default AdminPriceListCustomData;